<template>
  <div class="search" v-bind:class="{'iphone': iOS}" v-hotkey="keymap">
    <div class="container pt-3 pt-md-0">
      <spinner v-if="dataLoading && !serverError"></spinner>
      <div class="raised-frame px-4 pt-3 pb-4 w-75 ml-auto mr-auto mt-4 bg-white h-100">
        <h2 class="mb-0">
          <img src="@/assets/text-icon.png" alt />
          {{hebrew ? 'הזן כאן טקסט על מנת למצוא אזכורים' : 'Enter text here to locate citations'}}
        </h2>
        <div
          v-bind:class="[emptyText ? 'border-danger' : 'border-dark', 'bg-white text-wrap border rounded w-100 mt-md-2 p-2 position-relative']"
        >
          <div            
            class="erase position-absolute p-2"
            id = "del-btn"
            v-if="searchText && searchText !== ''"
            @click="clearText"
          >
            <span aria-hidden="true">&times;</span>
          </div>
          <ResetDialog v-model="showReset" v-if="showReset" v-on:dialogClick="clearText"></ResetDialog>
          <textarea
            id="textEntryArea"
            ref="textArea"
            @input="checkText"
            @focus="focusedEvent"
            @focusout="blurEvent"
            class="f-narkis w-100 border-0 pl-3 text-right"
            v-model="searchText"
          ></textarea>
          <button
            id="findInstancesBttn"
            class="btn btn-primary btn-lg d-block position-relative"
            style="z-index:1"
            v-bind:class="{'mr-auto': hebrew, 'ml-auto': !hebrew}"
            @click="search"
          >{{hebrew ? 'מצא אזכורים' : 'Find Citations'}}</button>
        </div>
        <b-modal
          centered
          size="sm"
          hide-backdrop
          body-class="d-flex flex-column p-3"
          header-class="bg-secondary text-body"
          v-model="showError"
          okVariant="secondary"
          ok-only
          :ok-title="hebrew ? 'סגור' : 'Close'"
        >
          <template slot="modal-header">{{hebrew ? 'הודעת מערכת' : 'System message'}}</template>
          <span
            v-if="shortText"
          >{{hebrew ? 'יש להזין מינימום 3 מילים כדי לקבל תוצאות' : 'Please enter at least three words'}}</span>
          <span
            v-if="longText"
          >{{hebrew ? 'נא להזין טקסט קצר יותר' : 'Please enter a shorter text'}}</span>
          <span
            v-if="notHebrew"
          >{{hebrew ? 'יש להזין טקסט בעברית' : 'Please enter Hebrew text only'}}</span>
          <span
            v-if="serverError"
          >{{hebrew ? 'לא ניתן לגשת כעת לשרת, נסה שוב מאוחר יותר' : 'Server error. Please try again later'}}</span>
          <div
            class="border-top border-secondary pt-3 mt-3"
            v-if="lessTanakhResultsNum > 1"
            v-html="hebrew ? 'נמצאו '+lessTanakhResultsNum+' פסוקים בתנ״ך שמכילים את הפסוקים שחיפשת' : 'There are '+lessTanakhResultsNum+' pesukim in the Tanakh containing the words you searched'"
          ></div>
          <div
            class="border-top border-secondary pt-3 mt-3"
            v-if="lessTanakhResultsNum == 1"
          >{{hebrew ? 'קיים פסוק אחד בתנ"ך שמכיל את המילה שחיפשת' : 'There is a Pasuk in kh containing the word you searched for'}}</div>
          <a
            class="d-block border-bottom border-secondary pb-3 mb-3"
            v-if="lessTanakhResultsNum > 0"
            target="_blank"
            :href="hebrew ? 'https://search.dicta.org.il/result?text='+searchText : 'https://search.dicta.org.il/result?text='+searchText+'&lang=en'"
          >
            {{hebrew ? 'חפש בתנ״ך' : 'Search the Bible'}}
            <i
              v-if="hebrew"
              class="fa fa-arrow-left"
              aria-hidden="true"
            ></i>
            <i v-else class="fa fa-arrow-right" aria-hidden="true"></i>
          </a>

          <div
            v-bind:class="{ 'border-top border-secondary pt-3 mt-3': lessTanakhResultsNum == 0 }"
            v-if="lessTalmudResultsNum > 1"
            v-html="hebrew ? 'נמצאו '+lessTalmudResultsNum+' פסוקים בתלמוד שמכילים את הפסוקים שחיפשת' : 'There are '+lessTalmudResultsNum+' pesukim in the Talmud containing the words you searched'"
          ></div>
          <div
            v-bind:class="{ 'border-top border-secondary pt-3 mt-3': lessTanakhResultsNum == 0 }"
            v-if="lessTalmudResultsNum == 1"
          >{{hebrew ? 'קיים פסוק אחד בתלמוד שמכיל את המילה שחיפשת' : 'There is a Pasuk in Talmud containing the word you searched for'}}</div>
          <a
            class="d-block border-bottom border-secondary pb-3 mb-3"
            v-if="lessTalmudResultsNum > 0"
            target="_blank"
            :href="hebrew ? 'https://talmudsearch.dicta.org.il/result?text='+searchText : 'https://talmudsearch.dicta.org.il/result?text='+searchText+'&lang=en'"
          >
            {{hebrew ? 'חפש בתלמוד' : 'Search in Talmud'}}
            <i
              v-if="hebrew"
              class="fa fa-arrow-left"
              aria-hidden="true"
            ></i>
            <i v-else class="fa fa-arrow-right" aria-hidden="true"></i>
          </a>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { Actions } from "@/store/stateChanges";
import Spinner from "@/components/spinner";
import ResetDialog from "@/components/ResetDialog";
export default {
  name: "Search",
  props: {},
  components: { Spinner, ResetDialog },
  data() {
    return {
      searchText: '',
      showError: false,
      emptyText: false,
      shortText: false,
      longText: false,
      notHebrew: false,
      dataLoading: false,
      showReset: false,
      DontAskAgain: false,
      iOS: false
    };
  },
  mounted: function() {
    this.DontAskAgain = localStorage.NOT_ASK
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    this.iOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!this.searchText && !isMobile) {
      this.$refs.textArea.focus();
    }
    if (isMobile && !this.iOS) {
      const orgTextHeight = document.getElementById("textEntryArea").clientHeight
      window.addEventListener("resize", function() {
        // If the current active element is a text input, we can assume the soft keyboard is visible.
        if (document.getElementById("textEntryArea").clientHeight < orgTextHeight) {
          document.body.classList.add("search-focused")
        } else {
          document.body.classList.remove("search-focused")
        }
      });
    }
  },
  computed: {
    keymap () {
      return {
        'ctrl+enter': () => { 
          this.search()
        }
      }
    },    
    hebrew() {
      return this.$settings.hebrew;
    },
    serverError: function() {
      return this.$store.state.serverError;
    },
    complete: function() {
      return this.$store.state.footnotesComplete;
    },
    lessTalmudResultsNum: function() {
      return this.$store.state.extTalmudRes;
    },
    lessTanakhResultsNum: function() {
      return this.$store.state.extTanakhRes;
    },
    data: function() {
      return this.$store.state.selectedResults;
    }
  },
  watch: {
    serverError: function(val) {
      if (val) {
        this.showError = true;
        console.log(this.$store.state.serverError);
      }
    },
    complete: function(val) {
      if (val) {
        this.$router.push({ name: "Results" });
      }
    }
  },
  methods: {
    focusedEvent () {
      document.body.classList.add("search-focused")
      this.$refs.textArea.focus();
    },
    blurEvent () {
      if (this.iOS) {
         setTimeout(function() {
          document.activeElement.blur()
          document.body.classList.remove("search-focused")          
         }, 200);
       }
    },
    clearErrors () {
      this.shortText = false;
      this.longText = false;
      this.notHebrew = false;
    },
    clearText () { //value former param
      //if (value.clicked || this.DontAskAgain) {
        this.DontAskAgain = true;
        this.$store.commit("SET_TEXT_FOR_API_CALL", "");
        this.searchText = "";
        this.$refs.textArea.focus();
        this.clearErrors();
      //}
    },
    openReset () {
      if (this.iOS)
        document.body.classList.remove("search-focused")
      if (!this.DontAskAgain) this.showReset = true;
      else {
        this.clearText(true);
      }
    },
    search () {
      this.$store.commit("SET_SERVER_ERROR",'')
      document.body.classList.remove("search-focused")
      // this.searchText = this.searchText.replace(/\s+/g, ' ').trim()
      if (this.searchText.length>0) {                
        let textLength = this.searchText.split(" ").length;
        this.$store.commit("SET_TEXT_FOR_API_CALL", this.searchText);
        if (
          !this.searchText
            .split("")
            .filter(c => "קראטוןםפשדגכעיחלךףזסבהנמצתץ".includes(c)).length
        ) {
          this.notHebrew = true;
          this.showError = true;
        } else if (this.searchText.length >= 10000) {
          this.longText = true;
          this.showError = true;
        } else if (textLength < 3) {
          this.shortText = true;
          this.showError = true;
          this.$store.dispatch(Actions.RUN_TANAKH_SEARCH);
          this.$store.dispatch(Actions.RUN_TALMUD_SEARCH);
        } else {
          this.$store.commit("SET_SELECTED_RESULTS", []);
          this.$store.dispatch(Actions.RUN_FOOTNOTES);
          this.dataLoading = true;
        }
      } else {
        this.emptyText = true;
        this.$refs.textArea.focus();
      }
    },
    checkText () {
      this.emptyText = this.searchText === "";
    }
  }
};
</script>
<style scoped lang=scss>
h2 {
  direction: ltr;
}
.he h2 {
  direction: rtl;
}
.container {
  height: calc(100vh - 130px);
  position: relative;
  z-index: 2;
}
.promotions-displayed .container{
  height: calc(100vh - 160px);
}
.raised-frame {
  min-height: 200px;
  overflow-y: auto;
}
.erase {
  left: 5px;
  top: 0;
  font-size: 20px;
  @media only screen and (max-width: 991px) {
    span {
      font-size: 34px;
    }
  }
}
.text-wrap {
  height: calc(100% - 45px);

  &:focus-within {
    border-color: var(--primary) !important;
  }
}

textarea {
  height: calc(100% - 45px);
  direction: rtl;
  font-size: 18px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
  resize: none;
  &:focus {
    outline: none;
  }
}
</style>
<style lang="scss">
@media only screen and (max-width: 992px) {
  .search .raised-frame {
    width: 100% !important;
  }
}
@media only screen and (max-width: 767px) {

  body {
    &.promotions-displayed {
      .search {
         .raised-frame {
            height: calc(100vh - 230px)!important;
            min-height: calc(100vh - 230px)!important;
         }
      }
    }
    .search {
      .container {
        height: 100% !important;
      }
      .text-wrap {
        height: calc(100% - 8px);
        height: 100%;
      }
      .raised-frame {
        margin: 0 !important;
        padding: 0 !important;

        h2 {
          display: none;
        }
        height: calc(100vh - 150px)!important;
        min-height: calc(100vh - 150px)!important;
      }
      &.iphone {
        .raised-frame {
          height: calc(100vh - 228px)!important;
          min-height: calc(100vh - 228px)!important;
        }
      }      
    }
    &.search-focused {    
      .search {
     /*    .raised-frame {
          height: calc(100vh - 140px)!important;
          min-height: calc(100vh - 140px)!important;
        }        
        .container {
          padding: 0 2px;
        } */
        &.iphone {
          .raised-frame {
            min-height: 40vh !important;
            height: 40vh !important;
          }
        }
      }
    }
  }
}
</style>